/**
 * nativeDirectPrint.ts
 *
 * Usa el MISMO patrón exacto que SunmiTestPage (que SÍ imprime):
 *   1. await import('@capacitor/core')
 *   2. registerPlugin('SunmiPrinter')  ← FRESCO cada vez, sin caché
 *   3. await plugin.printText({ text }) ← directo, sin wrappers
 *
 * NO usa: printViaSunmi, getSunmiPlugin, withTimeout, Promise.race, caché de plugin.
 *
 * [BROKEN PATH] (eliminado): nativeDirectPrint → import('./sunmiPrinter') → printViaSunmi
 *   → getSunmiPlugin (caché _plugin) → withTimeout → plugin.printText
 *   Causa: plugin cacheado stale + doble timeout anidado = congelamiento
 *
 * [WORKING PATH] (actual): nativeDirectPrint → import('@capacitor/core')
 *   → registerPlugin fresco → plugin.printText directo
 *   Idéntico a SunmiTestPage handlePrintSimple que SÍ funciona.
 */

import { payloadToTicketText, ticketPayloadToEscPosBytes, ticketPayloadToSunmiCommands } from './escposPrinter';

export interface DirectPrintResult {
  ok: boolean;
  error?: string;
}

/**
 * Función mínima que replica EXACTAMENTE el patrón de SunmiTestPage.
 * Cero wrappers. Cero caché. Cero timeouts extra.
 */
export async function sunmiPrintRaw(text: string, tag: string): Promise<DirectPrintResult> {
  console.log(`[${tag}] importando @capacitor/core...`);
  const { registerPlugin } = await import('@capacitor/core');

  console.log(`[${tag}] registerPlugin('SunmiPrinter') fresco...`);
  const plugin: any = registerPlugin('SunmiPrinter');

  console.log(`[${tag}] plugin.printText (${text.length} chars)...`);
  const result = await plugin.printText({ text });

  console.log(`[${tag}] resultado:`, JSON.stringify(result));
  if (result?.success === true) {
    return { ok: true };
  }
  return { ok: false, error: result?.error || `stage: ${result?.stage || 'unknown'}` };
}

/**
 * Envía bytes ESC/POS crudos al plugin SUNMI via printRawData (Base64).
 * Permite formateo avanzado: bold, tamaño, centrado.
 */
export async function sunmiPrintRawBytes(bytes: Uint8Array, tag: string): Promise<DirectPrintResult> {
  console.log(`[${tag}] importando @capacitor/core...`);
  const { registerPlugin } = await import('@capacitor/core');

  console.log(`[${tag}] registerPlugin('SunmiPrinter') fresco...`);
  const plugin: any = registerPlugin('SunmiPrinter');

  // Convertir bytes a Base64 para enviar via plugin
  const binary = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
  const base64 = btoa(binary);
  console.log(`[${tag}] plugin.printRawData (${bytes.length} bytes, ${base64.length} b64 chars)...`);

  const result = await plugin.printRawData({ data: base64 });

  console.log(`[${tag}] resultado:`, JSON.stringify(result));
  if (result?.success === true) {
    return { ok: true };
  }
  return { ok: false, error: result?.error || `stage: ${result?.stage || 'unknown'}` };
}

/**
 * Envía comandos estructurados al plugin SUNMI via printFormattedTicket.
 * Usa SDK printText (TrueType → sin slashed zero) en vez de sendRAWData (character ROM).
 */
export async function sunmiPrintFormatted(commandsJson: string, tag: string): Promise<DirectPrintResult> {
  console.log(`[${tag}] importando @capacitor/core...`);
  const { registerPlugin } = await import('@capacitor/core');
  const plugin: any = registerPlugin('SunmiPrinter');
  console.log(`[${tag}] plugin.printFormattedTicket (${commandsJson.length} chars)...`);
  const result = await plugin.printFormattedTicket({ commands: commandsJson });
  console.log(`[${tag}] resultado:`, JSON.stringify(result));
  if (result?.success === true) {
    return { ok: true };
  }
  return { ok: false, error: result?.error || `stage: ${result?.stage || 'unknown'}` };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. IMPRIMIR TICKET (POS)
// ─────────────────────────────────────────────────────────────────────────────

export async function printTicketNativeDirect(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  leyendaLocal?: string;
  localTelefono?: string;
  estado?: string;
}): Promise<DirectPrintResult> {
  const TAG = 'TICKET';
  console.log(`[${TAG}] ─────────────────────────────────`);

  if (!payload.ticketCode || !payload.jugadas?.length) {
    console.error(`[${TAG}] payload inválido`);
    return { ok: false, error: 'Payload incompleto' };
  }

  // Debug phone field to trace why it might not print
  console.log(`[${TAG}] localTelefono:`, JSON.stringify(payload.localTelefono));

  try {
    // SDK formateado: sin slashed-zero, números centrales más grandes, MONAZOS limpio
    const commands = ticketPayloadToSunmiCommands(payload);
    console.log(`[${TAG}] SDK commands: ${commands.length} chars`);
    const result = await sunmiPrintFormatted(commands, TAG);
    if (result.ok) {
      console.log(`[${TAG}] resultado: OK`);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return result;
    }
    console.warn(`[${TAG}] SDK path failed: ${result.error}, trying ESC/POS...`);
  } catch (err: any) {
    console.warn(`[${TAG}] SDK path error:`, err?.message);
  }

  try {
    // Fallback ESC/POS raw bytes
    const bytes = ticketPayloadToEscPosBytes(payload);
    console.log(`[${TAG}] ESC/POS bytes: ${bytes.length}`);
    const result = await sunmiPrintRawBytes(bytes, TAG);
    console.log(`[${TAG}] resultado:`, result.ok ? 'OK' : result.error);
    console.log(`[${TAG}] ─────────────────────────────────`);
    return result;
  } catch (err: any) {
    // Fallback final: texto plano
    console.warn(`[${TAG}] ESC/POS falló, usando texto plano:`, err?.message);
    try {
      const text = payloadToTicketText(payload);
      const result = await sunmiPrintRaw(text, TAG);
      console.log(`[${TAG}] fallback resultado:`, result.ok ? 'OK' : result.error);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return result;
    } catch (fbErr: any) {
      const msg = fbErr?.message || String(fbErr);
      console.error(`[${TAG}] EXCEPCION fallback:`, msg);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return { ok: false, error: msg };
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. REIMPRIMIR TICKET
// ─────────────────────────────────────────────────────────────────────────────

export async function reprintTicketNativeDirect(payload: {
  ticketCode: string;
  sorteoNombre: string;
  fecha?: string;
  total: number;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number; tipoSorteo?: string }>;
  localNombre?: string;
  localDescripcion?: string;
  sorteoCierre?: string;
  vendedorNombre?: string;
  clienteNombre?: string;
  pagaVecesAplicado?: string | null;
  leyendaLocal?: string;
  localTelefono?: string;
  estado?: string;
}): Promise<DirectPrintResult> {
  const TAG = 'REAL BUTTON REPRINT';
  console.log(`[${TAG}] ─────────────────────────────────`);

  if (!payload.ticketCode) {
    console.error(`[${TAG}] payload inválido`);
    return { ok: false, error: 'Sin ticketCode' };
  }

  try {
    // SDK formateado: sin slashed-zero, números centrales más grandes, MONAZOS limpio
    const commands = ticketPayloadToSunmiCommands({ ...payload, esReimpresion: true });
    console.log(`[${TAG}] SDK commands: ${commands.length} chars`);
    const result = await sunmiPrintFormatted(commands, TAG);
    if (result.ok) {
      console.log(`[${TAG}] resultado: OK`);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return result;
    }
    console.warn(`[${TAG}] SDK path failed: ${result.error}, trying ESC/POS...`);
  } catch (err: any) {
    console.warn(`[${TAG}] SDK path error:`, err?.message);
  }

  try {
    // Fallback ESC/POS raw bytes
    const bytes = ticketPayloadToEscPosBytes({ ...payload, esReimpresion: true });
    console.log(`[${TAG}] ESC/POS bytes: ${bytes.length}`);
    const result = await sunmiPrintRawBytes(bytes, TAG);
    console.log(`[${TAG}] resultado:`, result.ok ? 'OK' : result.error);
    console.log(`[${TAG}] ─────────────────────────────────`);
    return result;
  } catch (err: any) {
    // Fallback final: texto plano
    console.warn(`[${TAG}] ESC/POS falló, usando texto plano:`, err?.message);
    try {
      const text = payloadToTicketText({ ...payload, esReimpresion: true });
      const result = await sunmiPrintRaw(text, TAG);
      console.log(`[${TAG}] fallback resultado:`, result.ok ? 'OK' : result.error);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return result;
    } catch (fbErr: any) {
      const msg = fbErr?.message || String(fbErr);
      console.error(`[${TAG}] EXCEPCION fallback:`, msg);
      console.log(`[${TAG}] ─────────────────────────────────`);
      return { ok: false, error: msg };
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. IMPRIMIR LISTA CONSOLIDADA
// ─────────────────────────────────────────────────────────────────────────────

export interface ListPrintData {
  localNombre: string;
  sorteoNombre: string;
  tipo: string;
  fecha: string;
  totalVendedores: number;
  totalJugadas: number;
  totalMonto: number;
  filas: Array<Record<string, any>>;
  columnas: Array<{ key: string; label: string }>;
}

export function listDataToText(data: ListPrintData): string {
  const lines: string[] = [];
  const hr = '================================';
  lines.push('');
  lines.push((data.localNombre || 'LOCAL').toUpperCase());
  lines.push(`${data.sorteoNombre || ''} (${data.tipo || ''})`);
  lines.push(hr);
  lines.push(`FECHA: ${data.fecha}`);
  lines.push(`JUGADAS: ${data.filas.length}`);
  lines.push(`VENDEDORES: ${data.totalVendedores || 0}`);
  lines.push(hr);

  if (data.tipo === 'NORMAL') {
    lines.push('NUM  VENDIDO');
    lines.push('--------------------------------');
    for (const row of data.filas) {
      const num = String(row.numero ?? '').padEnd(5).slice(0, 5);
      const vendido = String(row.vendido ?? '').padStart(10).slice(-10);
      lines.push(`${num}${vendido}`);
    }
  } else {
    const headerRow = data.columnas.map(c => c.label.slice(0, 8).padEnd(8)).join('');
    lines.push(headerRow);
    lines.push('--------------------------------');
    for (const row of data.filas) {
      const vals = data.columnas.map(c => String(row[c.key] ?? '').slice(0, 8).padEnd(8));
      lines.push(vals.join(''));
    }
  }

  lines.push(hr);
  lines.push(`MONTO TOTAL: C${Math.trunc(data.totalMonto || 0).toLocaleString('es-CR')}`);
  lines.push(`JUGADAS: ${data.totalJugadas || 0}`);
  lines.push(hr);
  lines.push('TIEMPOS BARAHONA');
  lines.push('\n\n');

  return lines.join('\n');
}

export async function printListNativeDirect(data: ListPrintData): Promise<DirectPrintResult> {
  const TAG = 'REAL BUTTON LIST';
  console.log(`[${TAG}] ─────────────────────────────────`);

  if (!data.filas?.length) {
    console.error(`[${TAG}] sin filas`);
    return { ok: false, error: 'Lista vacía' };
  }

  const text = listDataToText(data);
  console.log(`[${TAG}] texto: ${text.length} chars`);

  try {
    const result = await sunmiPrintRaw(text, TAG);
    console.log(`[${TAG}] resultado final:`, result.ok ? 'OK' : result.error);
    console.log(`[${TAG}] ─────────────────────────────────`);
    return result;
  } catch (err: any) {
    const msg = err?.message || String(err);
    console.error(`[${TAG}] EXCEPCION:`, msg);
    console.log(`[${TAG}] ─────────────────────────────────`);
    return { ok: false, error: msg };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. IMPRESION RAW DIRECTA (para páginas que ya tienen su propio texto)
// ─────────────────────────────────────────────────────────────────────────────

export async function sunmiPrintRawWorking(text: string): Promise<DirectPrintResult> {
  return sunmiPrintRaw(text, 'RAW WORKING');
}
